import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { plural } from "./roster";
import styles from "./SourceTab.module.css";

/**
 * The one question this tab asks.
 *
 * Switching to an EMPTY table stops bot creation altogether: the population
 * worker asks the source for a row, gets nothing, and every guild below the
 * fill threshold simply stays there. That is a legitimate thing to want — the
 * mode is remembered and the CSV can arrive afterwards — but it is not a thing
 * to do silently, and the number that makes it real is how many guilds are
 * waiting right now.
 *
 * The reverse switch asks nothing. Going back to Generated breaks nothing: the
 * rows stay, the bots that came out of them stay, and the remaining rows are
 * picked up where they were left.
 *
 * Mockup: t-103/bots-source.html §5.
 */
export function SwitchSourceModal({
  guildsWaiting,
  saving,
  onCancel,
  onConfirm,
}: {
  /**
   * summary.guilds_waiting — guilds the worker would fill this minute, or
   * null when the table could not be read at all. Null is NOT zero: «no guild
   * is waiting» is a fact, and printing it when the request that would have
   * established it failed would be inventing one.
   */
  guildsWaiting: number | null;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      open
      title="Switch the source to the table?"
      onClose={onCancel}
      closeDisabled={saving}
      footer={
        <>
          <span className={styles.footNote}>Reversible at any time.</span>
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onConfirm} loading={saving}>
            Switch anyway
          </Button>
        </>
      }
    >
      <Alert
        variant="warn"
        title="The table is empty — no bot will be created"
      >
        {guildsWaiting === null ? (
          <>
            This project's table could not be read just now, so there is no
            telling how many guilds are waiting — but no bot will be created
            until it has rows in it. Bots already in guilds are not affected.
          </>
        ) : guildsWaiting > 0 ? (
          <>
            <b>{plural(guildsWaiting, "guild", "guilds")}</b>{" "}
            {guildsWaiting === 1 ? "is" : "are"} below the fill threshold right
            now and will stay there until the table has rows in it. Bots
            already in guilds are not affected.
          </>
        ) : (
          <>
            No guild is below the fill threshold this minute, so nothing stops
            today — but the next one that falls below it will stay there until
            the table has rows in it. Bots already in guilds are not affected.
          </>
        )}
      </Alert>
      <p className={styles.helper}>
        You can switch now and import the CSV afterwards — the mode is
        remembered.
      </p>
    </Modal>
  );
}
